import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [desktopMediaScroller] - The desktop column containing the full-size media.
 * @property {HTMLElement} [desktopThumbnails] - The independently scrollable desktop thumbnail column.
 * @property {HTMLButtonElement[]} [desktopThumbnail] - The desktop thumbnail buttons.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupDesktopGallery();
  }

  #controller = new AbortController();
  #desktopScrollFrame = 0;
  #activeDesktopMediaIndex = -1;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    window.cancelAnimationFrame(this.#desktopScrollFrame);
  }

  #setupDesktopGallery() {
    const { desktopMediaScroller } = this.refs;
    if (!(desktopMediaScroller instanceof HTMLElement) || !this.media?.length) return;

    desktopMediaScroller.addEventListener('scroll', this.#handleDesktopMediaScroll, {
      passive: true,
      signal: this.#controller.signal,
    });

    window.addEventListener('resize', this.#handleDesktopMediaScroll, {
      passive: true,
      signal: this.#controller.signal,
    });

    this.#desktopScrollFrame = window.requestAnimationFrame(() => this.#updateActiveDesktopMedia(true));
  }

  #handleDesktopMediaScroll = () => {
    window.cancelAnimationFrame(this.#desktopScrollFrame);
    this.#desktopScrollFrame = window.requestAnimationFrame(() => this.#updateActiveDesktopMedia());
  };

  /**
   * Selects a full-size desktop media item from the independent thumbnail column.
   *
   * @param {number | string} index - The zero-based media index.
   */
  selectDesktopMedia(index) {
    const mediaIndex = Number(index);
    const { desktopMediaScroller } = this.refs;
    const targetMedia = this.media?.[mediaIndex];

    if (
      !Number.isInteger(mediaIndex) ||
      !(desktopMediaScroller instanceof HTMLElement) ||
      !(targetMedia instanceof HTMLElement)
    ) {
      return;
    }

    const scrollerRect = desktopMediaScroller.getBoundingClientRect();
    const targetRect = targetMedia.getBoundingClientRect();
    const targetTop = desktopMediaScroller.scrollTop + targetRect.top - scrollerRect.top;

    desktopMediaScroller.scrollTo({
      top: targetTop,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });

    this.#setActiveDesktopMedia(mediaIndex, true);
  }

  /**
   * Finds the media item nearest to the center of the full-size media column.
   *
   * @param {boolean} instantThumbnailScroll - Whether the thumbnail strip should move without animation.
   */
  #updateActiveDesktopMedia(instantThumbnailScroll = false) {
    const { desktopMediaScroller } = this.refs;
    if (!(desktopMediaScroller instanceof HTMLElement) || !this.media?.length) return;

    const scrollerRect = desktopMediaScroller.getBoundingClientRect();
    const scrollerCenter = scrollerRect.top + scrollerRect.height / 2;
    let activeIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.media.forEach((media, index) => {
      const rect = media.getBoundingClientRect();
      const mediaCenter = rect.top + rect.height / 2;
      const distance = Math.abs(mediaCenter - scrollerCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeIndex = index;
      }
    });

    this.#setActiveDesktopMedia(activeIndex, instantThumbnailScroll);
  }

  /**
   * Marks a thumbnail active and keeps it visible inside its own scroll column.
   *
   * @param {number} index - The zero-based media index.
   * @param {boolean} instantThumbnailScroll - Whether to avoid animation.
   */
  #setActiveDesktopMedia(index, instantThumbnailScroll = false) {
    if (index === this.#activeDesktopMediaIndex) return;

    const { desktopThumbnail, desktopThumbnails } = this.refs;
    if (!desktopThumbnail?.length) return;

    this.#activeDesktopMediaIndex = index;

    desktopThumbnail.forEach((thumbnail, thumbnailIndex) => {
      const isActive = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-current', String(isActive));
    });

    const activeThumbnail = desktopThumbnail[index];
    if (!(activeThumbnail instanceof HTMLElement) || !(desktopThumbnails instanceof HTMLElement)) return;

    const stripRect = desktopThumbnails.getBoundingClientRect();
    const thumbnailRect = activeThumbnail.getBoundingClientRect();
    const thumbnailOutsideView = thumbnailRect.top < stripRect.top || thumbnailRect.bottom > stripRect.bottom;
    if (!thumbnailOutsideView) return;

    const targetTop =
      desktopThumbnails.scrollTop +
      thumbnailRect.top -
      stripRect.top -
      (stripRect.height - thumbnailRect.height) / 2;

    desktopThumbnails.scrollTo({
      top: targetTop,
      behavior: instantThumbnailScroll || prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
